import React, { useState, useEffect, useCallback, useRef } from 'react';
import { COINS } from './constants';
import usePrices from './hooks/usePrices';
import useBirdeyeHistory from './hooks/useBirdeyeHistory';
import useCountdown from './hooks/useCountdown';
import useTimeSlots from './hooks/useTimeSlots';
import useWallet from './hooks/useWallet';
import useServerTime from './hooks/useServerTime';
import CoinColumn from './components/CoinColumn';
import Dashboard from './components/Dashboard';
import Navbar from './components/Navbar';
import LandingPage from './components/landing/LandingPage';
import AdminDashboard from './components/admin/AdminDashboard';
import ContactPage from './components/ContactPage';
import {
    getMe, placeBet as apiPlaceBet, logout as apiLogout,
    getActivePools, getUserActiveBets, getUserSettlements, getSettledRounds,
    getDepositWallet, verifyDeposit,
} from './lib/api';

/**
 * App — Root orchestration shell.
 *
 * ALL state comes from the server. No localStorage for business data.
 * Client only handles: view state, price display, UI interactions.
 * Auth → JWT cookie. Balance → DB. Bets → DB. Ledger → DB.
 */
const App = () => {
    // ── Auth state (from server JWT) ──
    const [user, setUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);

    // ── UI state (client-only, not sensitive) ──
    const [view, setView] = useState('trading');
    const [prices, setPrices] = useState({});
    const [priceHistory, setPriceHistory] = useState({});
    const [timeSlots, setTimeSlots] = useState({});
    const [countdown, setCountdown] = useState(60);
    const [isPaused, setIsPaused] = useState(false);
    const [graphCollapsed, setGraphCollapsed] = useState({});
    const [liveStartPrices, setLiveStartPrices] = useState({});
    const [settledHistory, setSettledHistory] = useState({});
    const [platformPaused, setPlatformPaused] = useState(false);
    const [activeMobileCoin, setActiveMobileCoin] = useState(COINS[0]?.id || 'bitcoin');

    // ── Poll platform pause status ──
    useEffect(() => {
        const checkStatus = async () => {
            try {
                const res = await fetch('/api/platform/status');
                const data = await res.json();
                setPlatformPaused(data.paused === true);
            } catch { /* ignore */ }
        };
        checkStatus();
        const interval = setInterval(checkStatus, 10000);
        return () => clearInterval(interval);
    }, []);

    // ── Wallet connection (post-login, lightweight — no SIWS re-auth) ──
    const { connecting: walletConnecting, sendDeposit } = useWallet();
    const [walletAddress, setWalletAddress] = useState(() =>
        localStorage.getItem('mm_wallet_address') || null
    );
    const [walletSolBalance, setWalletSolBalance] = useState(null);

    // Fetch SOL balance from chain
    const fetchWalletBalance = useCallback(async (addr) => {
        try {
            const { Connection, PublicKey } = await import('@solana/web3.js');
            const rpcUrl = process.env.REACT_APP_SOLANA_RPC || 'https://api.mainnet-beta.solana.com';
            const conn = new Connection(rpcUrl, 'confirmed');
            const lamports = await conn.getBalance(new PublicKey(addr));
            setWalletSolBalance(lamports / 1e9);
        } catch (err) {
            console.error('[Wallet] Balance fetch failed:', err.message);
            setWalletSolBalance(0);
        }
    }, []);

    // Auto-reconnect wallet on mount — never clear address on failure
    useEffect(() => {
        const saved = localStorage.getItem('mm_wallet_address');
        if (saved) {
            // Show address + fetch balance immediately from chain
            fetchWalletBalance(saved);
            // Silently reconnect Phantom if available
            if (window.solana?.isPhantom) {
                window.solana.connect({ onlyIfTrusted: true }).catch(() => {
                    // Phantom locked — address stays displayed, will reconnect on next user action
                });
            }
        }
    }, [fetchWalletBalance]);

    const connectPhantom = useCallback(async () => {
        try {
            const provider = window.solana;
            if (!provider?.isPhantom) {
                alert('Phantom wallet not found. Install at phantom.app');
                return;
            }
            const resp = await provider.connect();
            const addr = resp.publicKey.toString();
            setWalletAddress(addr);
            localStorage.setItem('mm_wallet_address', addr);
            fetchWalletBalance(addr);
        } catch (err) {
            console.error('[Wallet] Connect error:', err);
        }
    }, [fetchWalletBalance]);

    const disconnectPhantom = useCallback(() => {
        try {
            if (window.solana?.isPhantom) window.solana.disconnect();
        } catch { /* ignore */ }
        setWalletAddress(null);
        setWalletSolBalance(null);
        localStorage.removeItem('mm_wallet_address');
    }, []);

    // ── Server-synced authoritative time (Eastern) ──
    const { now: serverNow, getCountdown: serverGetCountdown, synced: timeSynced } = useServerTime();

    // ── Server-backed state ──
    const [serverPools, setServerPools] = useState({});
    const [userBets, setUserBets] = useState({});
    const [ledger, setLedger] = useState([]);
    const [balance, setBalance] = useState(0);
    const [emaOdds, setEmaOdds] = useState({}); // 1h trend from Binance klines (updated with pool poll)
    const [houseFee, setHouseFee] = useState(0);

    // ── Refs for stable callbacks ──
    const balanceRef = useRef(0);
    const userRef = useRef(null);
    const sessionClearedAtRef = useRef(null); // Timestamp of last session clear
    balanceRef.current = balance;
    userRef.current = user;

    // ── Check session on mount ──
    useEffect(() => {
        checkSession();
    }, []);

    async function checkSession() {
        try {
            const data = await getMe();
            setUser(data);
            setIsAdmin(data.isAdmin || false);
            setBalance(data.balance || 0);
        } catch {
            setUser(null);
            setIsAdmin(false);
        }
        setAuthLoading(false);
    }

    // ── Login handler (called by LandingPage/LoginForm) ──
    const handleEnter = useCallback((name, admin = false) => {
        loggedOutRef.current = false;
        // Re-check session from server after login
        checkSession();
    }, []);

    // ── Logout ──
    const loggedOutRef = useRef(false);
    const handleLogout = useCallback(async () => {
        loggedOutRef.current = true;
        try {
            await apiLogout();
        } catch { /* ignore */ }
        setUser(null);
        setIsAdmin(false);
        setBalance(0);
        setLedger([]);
        setUserBets({});
        setServerPools({});
        setEmaOdds({});
    }, []);

    // ── Poll server for pool data + user bets ──
    useEffect(() => {
        if (!user) return;

        const fetchServerState = async () => {
            if (loggedOutRef.current) return;
            try {
                // Refresh user balance
                const me = await getMe();
                setBalance(me.balance || 0);

                // Fetch active pools from DB
                const poolData = await getActivePools();
                const poolMap = {};
                const serverStartPrices = {};
                poolData.pools.forEach(p => {
                    const key = `${p.coin_market}_slot_${new Date(p.slot_time).getTime()}`;
                    poolMap[key] = {
                        up: parseFloat(p.total_up_sol) || 0,
                        down: parseFloat(p.total_down_sol) || 0,
                        status: p.status,
                        dbId: p.id,
                    };
                    // Use server's open_price as authoritative start price
                    if (p.status === 'locked' && p.open_price) {
                        const existing = serverStartPrices[p.coin_market];
                        const slotMs = new Date(p.slot_time).getTime();
                        // Use the most recent locked pool's open price
                        if (!existing || slotMs > existing.slotMs) {
                            const d = new Date(p.slot_time);
                            serverStartPrices[p.coin_market] = {
                                time: `${d.getHours() % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')}`,
                                start: parseFloat(p.open_price),
                                slotMs,
                            };
                        }
                    }
                });
                setServerPools(poolMap);

                // Store 1h trend data from same response (updates every 5s with pool poll)
                if (poolData.trends) setEmaOdds(poolData.trends);

                // Override client-captured start prices with server-authoritative ones
                if (Object.keys(serverStartPrices).length > 0) {
                    setLiveStartPrices(prev => {
                        const next = { ...prev };
                        Object.entries(serverStartPrices).forEach(([coin, data]) => {
                            next[coin] = { time: data.time, start: data.start };
                        });
                        return next;
                    });
                }

                // Fetch user's active bets
                const betData = await getUserActiveBets();
                const betMap = {};
                betData.bets.forEach(b => {
                    const key = `${b.coin_market}_slot_${new Date(b.slot_time).getTime()}`;
                    betMap[key] = {
                        direction: b.direction,
                        amount: Math.floor(parseFloat(b.amount_sol) * 100),
                        locked: true, // Already submitted to server
                        dbId: b.id,
                    };
                });
                setUserBets(betMap);

                // Fetch settlement history (ledger)
                const settleData = await getUserSettlements(100);
                let ledgerEntries = settleData.settlements.map(s => {
                    const slotDate = new Date(s.slot_time);
                    return {
                        // Use slot_time (bet time) NOT settled_at (engine run time)
                        time: slotDate.toLocaleString(),
                        displayTime: `${slotDate.getHours() % 12 || 12}:${String(slotDate.getMinutes()).padStart(2, '0')}`,
                        slotTimeMs: slotDate.getTime(),
                        settledAt: new Date(s.settled_at).toLocaleString(),
                        settledAtMs: new Date(s.settled_at).getTime(),
                        coin: s.coin_market.toUpperCase(),
                        direction: s.direction,
                        amount: parseFloat(s.amount_sol),
                        result: s.result,
                        net: s.result === 'WIN'
                            ? parseFloat(s.payout_sol) - parseFloat(s.amount_sol)
                            : s.result === 'PUSH' ? 0 : -parseFloat(s.amount_sol),
                        fee: parseFloat(s.house_fee_sol) || 0,
                        odds: ((parseFloat(s.payout_sol) + parseFloat(s.house_fee_sol || 0)) / parseFloat(s.amount_sol)).toFixed(2) + 'x',
                    };
                });

                // Filter out settlements from before session was cleared
                if (sessionClearedAtRef.current) {
                    ledgerEntries = ledgerEntries.filter(
                        e => e.settledAtMs > sessionClearedAtRef.current
                    );
                }
                setLedger(ledgerEntries);

                // Calculate house fee total from settlements
                const totalFee = ledgerEntries.reduce(
                    (acc, e) => acc + (e.fee || 0), 0
                );
                setHouseFee(totalFee);

                // Load settled rounds (open/close prices) from server
                // Server prices are authoritative — they override client captures
                try {
                    const roundsData = await getSettledRounds(10);
                    if (roundsData.rounds) {
                        setSettledHistory(prev => {
                            const next = { ...prev };
                            Object.entries(roundsData.rounds).forEach(([coin, rounds]) => {
                                // Re-format times from server timezone to local timezone
                                const localRounds = rounds.map(r => {
                                    if (r.slot_time) {
                                        const d = new Date(r.slot_time);
                                        return {
                                            ...r,
                                            time: `${d.getHours() % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')}`,
                                            slotTimeMs: d.getTime(),
                                        };
                                    }
                                    return r;
                                });
                                // Server data is authoritative — use it directly
                                // Only keep client entries for slots the server hasn't settled yet
                                const serverSlotMs = new Set(localRounds.map(r => r.slotTimeMs).filter(Boolean));
                                const serverTimes = new Set(localRounds.map(r => r.time));
                                const existing = next[coin] || [];
                                const clientOnly = existing.filter(e =>
                                    (e.slotTimeMs ? !serverSlotMs.has(e.slotTimeMs) : !serverTimes.has(e.time))
                                );
                                next[coin] = [...localRounds, ...clientOnly].slice(0, 10);
                            });
                            return next;
                        });
                    }
                } catch (roundsErr) {
                    // Non-critical — client-side captures still work
                    console.warn('[App] Settled rounds fetch failed:', roundsErr.message);
                }
            } catch (err) {
                // Server unreachable — keep stale data
                console.warn('[App] Server poll failed:', err.message);
            }
        };

        fetchServerState();
        const interval = setInterval(fetchServerState, 5000);
        return () => clearInterval(interval);
    }, [user]);

    // EMA odds now come from /api/pools/active response (see fetchServerState above).
    // No separate fetch needed — trends update every 5s with pool data.

    // ── Auto-seed live start prices on first price load (fallback until server locks pool) ──
    useEffect(() => {
        if (Object.keys(prices).length > 0 && Object.keys(liveStartPrices).length === 0) {
            const now = serverNow();
            const timeStr = `${now.getHours() % 12 || 12}:${String(now.getMinutes()).padStart(2, '0')}`;
            const starts = {};
            COINS.forEach(c => {
                if (prices[c.id]?.usd) starts[c.id] = { time: timeStr, start: prices[c.id].usd };
            });
            if (Object.keys(starts).length > 0) setLiveStartPrices(starts);
        }
    }, [prices, liveStartPrices, serverNow]);

    // ── Minute change handler — builds settled history for SettledBox ──
    const liveStartRef = useRef({});
    liveStartRef.current = liveStartPrices;

    const handleMinuteChange = useCallback((capturedPrices, timeStr) => {
        const prevStarts = liveStartRef.current;

        // Record the just-closed minute as a settled history entry
        setSettledHistory(prev => {
            const next = { ...prev };
            COINS.forEach(c => {
                const startData = prevStarts[c.id];
                const endPrice = capturedPrices[c.id]?.usd;
                if (startData?.start && endPrice) {
                    const entries = next[c.id] || [];
                    entries.unshift({
                        time: startData.time,
                        start: startData.start,
                        end: endPrice,
                    });
                    next[c.id] = entries.slice(0, 10); // Keep last 10
                }
            });
            return next;
        });

        // Set start prices for the new minute
        const nextStarts = {};
        COINS.forEach(c => {
            if (capturedPrices[c.id]?.usd) {
                nextStarts[c.id] = { time: timeStr, start: capturedPrices[c.id].usd };
            }
        });
        setLiveStartPrices(nextStarts);
    }, []);

    // ── Wire up data hooks (server-synced time) ──
    usePrices(setPrices, setPriceHistory);
    useBirdeyeHistory(setPriceHistory);
    useCountdown(setCountdown, isPaused, serverGetCountdown);
    useTimeSlots(prices, setTimeSlots, handleMinuteChange, isPaused, serverNow);

    // ── Bet callbacks (server-backed) ──
    const [pendingBets, setPendingBets] = useState({});

    const handleDotClick = useCallback((coinId, slotId, direction) => {
        const key = `${coinId}_${slotId}`;
        setPendingBets(p => ({
            ...p,
            [key]: { ...(p[key] || {}), direction, amount: p[key]?.amount || '' }
        }));
    }, []);

    const handleBetChange = useCallback((coinId, slotId, val) => {
        const key = `${coinId}_${slotId}`;
        const credits = Math.floor(balanceRef.current * 100);
        if (val !== '' && Number(val) > credits) {
            setPendingBets(p => ({ ...p, [key]: { ...p[key], amount: '' } }));
            return;
        }
        setPendingBets(p => ({ ...p, [key]: { ...p[key], amount: val } }));
    }, []);

    const submitBet = useCallback(async (coinId, slotId) => {
        const key = `${coinId}_${slotId}`;
        const bet = pendingBets[key];
        if (!bet?.direction || !bet?.amount || Number(bet.amount) <= 0) return;
        if (!userRef.current) return;

        const ts = parseInt(slotId.replace('slot_', ''));
        const slotTime = new Date(ts).toISOString();

        try {
            await apiPlaceBet(coinId, slotTime, bet.direction, Number(bet.amount) / 100);

            // Mark as locked (keep visible) instead of deleting
            setPendingBets(p => ({
                ...p,
                [key]: { ...p[key], locked: true }
            }));

            // Refresh balance + bets from server
            const me = await getMe();
            setBalance(me.balance || 0);
        } catch (err) {
            console.error('[Bet] Failed:', err.message);
        }
    }, [pendingBets]);

    // ── Merge server bets + pending bets for display ──
    const displayBets = { ...userBets, ...pendingBets };

    // ── Blended odds: w × trend + (1-w) × playerPool ──
    // w = 1 / (1 + BLEND_K × totalPoolSOL)
    // When pool is empty (w=1), odds = pure 1h trend.
    // As real money enters, player composition dominates.
    const BLEND_K = 8.0;
    const HOUSE_FEE_DISPLAY = 0.03; // 3% displayed fee multiplier

    const getPayoutRatio = useCallback((coinId, slotId, direction) => {
        const key = `${coinId}_${slotId}`;
        const pool = serverPools[key];
        const trend = emaOdds[coinId];

        // Get trend probability (default 0.5 if unavailable)
        const trendUp = trend?.up ?? 0.5;

        // Get player pool composition
        const poolUp = pool?.up ?? 0;
        const poolDown = pool?.down ?? 0;
        const totalPool = poolUp + poolDown;
        const playerUp = totalPool > 0 ? poolUp / totalPool : 0.5;

        // Blend: w decays as pool grows
        const w = 1 / (1 + BLEND_K * totalPool);
        const blendedUp = (w * trendUp) + ((1 - w) * playerUp);
        const blendedDown = 1 - blendedUp;

        // Payout multiplier for chosen direction
        const blendedSide = direction === 'UP' ? blendedUp : blendedDown;
        if (blendedSide <= 0) return '—';
        return ((1 - HOUSE_FEE_DISPLAY) / blendedSide).toFixed(2) + 'x';
    }, [serverPools, emaOdds]);

    const getSentimentRatio = useCallback((coinId, slotId) => {
        const key = `${coinId}_${slotId}`;
        const pool = serverPools[key];
        const trend = emaOdds[coinId];

        const trendUp = trend?.up ?? 0.5;
        const poolUp = pool?.up ?? 0;
        const poolDown = pool?.down ?? 0;
        const totalPool = poolUp + poolDown;
        const playerUp = totalPool > 0 ? poolUp / totalPool : 0.5;

        const w = 1 / (1 + BLEND_K * totalPool);
        const blendedUp = (w * trendUp) + ((1 - w) * playerUp);

        const downPct = Math.round((1 - blendedUp) * 100);
        const upPct = 100 - downPct;
        return `${downPct}/${upPct}`;
    }, [serverPools, emaOdds]);

    // ── Derived state ──
    const totalWinNet = ledger.filter(e => e.result === 'WIN')
        .reduce((acc, e) => acc + e.net, 0);
    const totalLossNet = ledger.filter(e => e.result === 'LOSS')
        .reduce((acc, e) => acc + Math.abs(e.net), 0);

    const handleClearSession = useCallback(async () => {
        if (!window.confirm('Clear session data? (Balance is unaffected)')) return;
        sessionClearedAtRef.current = Date.now();
        // Persist on server so it survives page refreshes
        try { await fetch('/api/session/clear', { method: 'POST', credentials: 'include' }); } catch {}
        setPendingBets({});
        setGraphCollapsed({});
        setSettledHistory({});
        setLedger([]);
        setUserBets({});
    }, []);

    // ── Render ──
    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{background:'var(--bg)'}}>
                <div className="font-mono text-sm animate-pulse" style={{color:'var(--text-2)'}}>Loading...</div>
            </div>
        );
    }

    if (!user) {
        return <LandingPage onEnter={handleEnter} />;
    }

    return (
        <div className={`h-screen text-gray-300 font-sans text-sm selection:bg-gray-700 flex flex-col ${
            view === 'admin' ? 'overflow-y-auto' : 'overflow-hidden'
        }`} style={{background:'var(--bg)'}}>
            {platformPaused && (
                <div className="bg-red-600 text-white text-center py-2 text-sm font-bold uppercase tracking-wider animate-pulse shrink-0">
                    ⚠️ Platform Maintenance — Betting is temporarily disabled ⚠️
                </div>
            )}
            <Navbar
                view={view}
                setView={setView}
                totalWinNet={totalWinNet}
                totalLossNet={totalLossNet}
                ledger={ledger}
                balance={balance}
                houseFee={houseFee}
                isPaused={isPaused}
                setIsPaused={setIsPaused}
                onClearSession={handleClearSession}
                username={user.displayName || user.wallet?.slice(0, 8) || 'User'}
                onLogout={handleLogout}
                isAdmin={isAdmin}
                walletAddress={walletAddress}
                walletSolBalance={walletSolBalance}
                onConnectWallet={connectPhantom}
                onDisconnectWallet={disconnectPhantom}
                walletConnecting={walletConnecting}
                onDeposit={async (amount) => {
                    try {
                        const { wallet: houseAddress } = await getDepositWallet();
                        const txSig = await sendDeposit(amount, houseAddress);
                        const result = await verifyDeposit(txSig, amount);
                        setBalance(result.newBalance || balance + amount);
                    } catch (err) {
                        throw err;
                    }
                }}
                onWithdraw={async (amountCredits, destinationWallet) => {
                    const res = await fetch('/api/withdrawals/request', {
                        method: 'POST',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ amountCredits, destinationWallet }),
                    });
                    const data = await res.json();
                    if (!res.ok) throw new Error(data.error || 'Withdrawal failed');
                    const balRes = await fetch('/api/auth/me', { credentials: 'include' });
                    if (balRes.ok) {
                        const me = await balRes.json();
                        if (me.user?.balance_sol != null) setBalance(me.user.balance_sol);
                    }
                    return data;
                }}
            />

            {/* Mobile coin tab bar — only on trading view, only on small screens */}
            {view === 'trading' && (
                <div className="coin-tabs-bar md:hidden">
                    {COINS.map(coin => {
                        const price = prices[coin.id]?.usd;
                        const dir = prices[coin.id]?.direction || 'flat';
                        const fmt = (p) => p
                            ? p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            : '—';
                        return (
                            <button
                                key={coin.id}
                                className={`coin-tab-btn ${activeMobileCoin === coin.id ? 'active' : ''}`}
                                onClick={() => setActiveMobileCoin(coin.id)}
                            >
                                <span className="coin-tab-sym">{coin.symbol}</span>
                                <span className="coin-tab-price">${fmt(price)}</span>
                                <span className={`coin-tab-chg ${dir}`}>
                                    {dir === 'up' ? '▲' : dir === 'down' ? '▼' : '—'}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* ── Main content area ── */}
            <div className="flex-1 min-h-0">
                {view === 'admin' && isAdmin ? (
                    <div className="overflow-y-auto h-full pb-16">
                        <AdminDashboard
                            houseFee={houseFee}
                            pools={serverPools}
                            bets={displayBets}
                            ledger={ledger}
                            prices={prices}
                        />
                    </div>
                ) : view === 'trading' ? (
                    <>
                        {/* Desktop: CSS grid — 3 equal columns, single row fills exact available height */}
                        <div className="hidden md:grid md:grid-cols-3 md:grid-rows-1 gap-3 px-3 pb-3 h-full overflow-hidden">
                            {COINS.map(coin => (
                                <CoinColumn
                                    key={coin.id}
                                    coin={coin}
                                    prices={prices}
                                    priceHistory={priceHistory}
                                    timeSlots={timeSlots}
                                    countdown={countdown}
                                    bets={displayBets}
                                    pools={serverPools}
                                    history={settledHistory[coin.id] || []}
                                    settlements={ledger.filter(e => e.coin === coin.id.toUpperCase())}
                                    liveStartPrice={liveStartPrices[coin.id]?.start}
                                    getSentimentRatio={getSentimentRatio}
                                    getPayoutRatio={getPayoutRatio}
                                    graphCollapsed={graphCollapsed}
                                    setGraphCollapsed={setGraphCollapsed}
                                    isPaused={isPaused}
                                    handleDotClick={handleDotClick}
                                    handleBetChange={handleBetChange}
                                    submitBet={submitBet}
                                    setBets={setPendingBets}
                                />
                            ))}
                        </div>
                        {/* Mobile: single column, tab-selected */}
                        <div className="md:hidden h-full">
                            {COINS.map(coin => (
                                <div
                                    key={coin.id}
                                    className={`h-full flex flex-col ${activeMobileCoin === coin.id ? '' : 'hidden'}`}
                                >
                                    <CoinColumn
                                        coin={coin}
                                        prices={prices}
                                        priceHistory={priceHistory}
                                        timeSlots={timeSlots}
                                        countdown={countdown}
                                        bets={displayBets}
                                        pools={serverPools}
                                        history={settledHistory[coin.id] || []}
                                        settlements={ledger.filter(e => e.coin === coin.id.toUpperCase())}
                                        liveStartPrice={liveStartPrices[coin.id]?.start}
                                        getSentimentRatio={getSentimentRatio}
                                        getPayoutRatio={getPayoutRatio}
                                        graphCollapsed={graphCollapsed}
                                        setGraphCollapsed={setGraphCollapsed}
                                        isPaused={isPaused}
                                        handleDotClick={handleDotClick}
                                        handleBetChange={handleBetChange}
                                        submitBet={submitBet}
                                        setBets={setPendingBets}
                                    />
                                </div>
                            ))}
                        </div>
                    </>
                ) : view === 'contact' ? (
                    <ContactPage onBack={() => setView('trading')} />
                ) : (
                    <Dashboard ledger={ledger} balance={balance} />
                )}
            </div>
        </div>
    );
};

export default App;
